import {
  Box,
  Tabs,
  Tab,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Checkbox,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';

const CHANNELS = ['CH1', 'CH2', 'CH3', 'CH4'];

export interface ChartControlsProps {
  chartType: 'bode' | 'nyquist' | 'delta' | 'combined' | 'calibration';
  onChartTypeChange: (type: 'bode' | 'nyquist' | 'delta' | 'combined' | 'calibration') => void;
  showCalibrationTab?: boolean;
  selectedTestCount: number;
  viewMode: 'baselines' | 'specimens' | 'both';
  onViewModeChange: (mode: 'baselines' | 'specimens' | 'both') => void;
  visibleChannels: Set<string>;
  onChannelToggle: (channel: string, checked: boolean) => void;
  phaseUnit: 'rad' | 'deg';
  onPhaseUnitChange: (unit: 'rad' | 'deg') => void;
  yScale: 'linear' | 'log';
  onYScaleChange: (scale: 'linear' | 'log') => void;
  deltaMode: 'bode' | 'nyquist';
  onDeltaModeChange: (mode: 'bode' | 'nyquist') => void;
}

export function ChartControls({
  chartType,
  onChartTypeChange,
  selectedTestCount,
  viewMode,
  onViewModeChange,
  visibleChannels,
  onChannelToggle,
  phaseUnit,
  onPhaseUnitChange,
  yScale,
  onYScaleChange,
  deltaMode,
  onDeltaModeChange,
  showCalibrationTab = false,
}: ChartControlsProps) {
  const combinedDisabled = selectedTestCount > 1;
  const isCalibration = chartType === 'calibration';

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 3,
        px: 2,
        py: 1,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      {/* Chart type tabs */}
      <Tabs
        value={chartType}
        onChange={(_, v) => onChartTypeChange(v as 'bode' | 'nyquist' | 'delta' | 'combined' | 'calibration')}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0.5 } }}
      >
        <Tab
          value="combined"
          disabled={combinedDisabled}
          sx={combinedDisabled ? { pointerEvents: 'auto' } : {}}
          label={
            <Tooltip
              title={combinedDisabled ? 'Select only one test to use Combined view' : ''}
              arrow
              placement="top"
            >
              <span>Combined</span>
            </Tooltip>
          }
        />
        <Tab label="Bode" value="bode" />
        <Tab label="Nyquist" value="nyquist" />
        <Tab label="Delta" value="delta" />
        {showCalibrationTab && <Tab label="Classification Calibration" value="calibration" />}
      </Tabs>

      {/* View mode — hidden for delta, combined, and calibration */}
      {!isCalibration && chartType !== 'delta' && chartType !== 'combined' && (
        <FormControl size="small">
          <FormLabel sx={{ fontSize: '0.75rem' }}>View</FormLabel>
          <RadioGroup
            row
            value={viewMode}
            onChange={(_, v) => onViewModeChange(v as 'baselines' | 'specimens' | 'both')}
          >
            <FormControlLabel value="baselines" control={<Radio size="small" />} label="BL" />
            <FormControlLabel value="specimens" control={<Radio size="small" />} label="SP" />
            <FormControlLabel value="both" control={<Radio size="small" />} label="Both" />
          </RadioGroup>
        </FormControl>
      )}

      {/* Delta sub-mode toggle */}
      {!isCalibration && chartType === 'delta' && (
        <FormControl size="small">
          <FormLabel sx={{ fontSize: '0.75rem' }}>Delta mode</FormLabel>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={deltaMode}
            onChange={(_, v) => v && onDeltaModeChange(v as 'bode' | 'nyquist')}
          >
            <ToggleButton value="bode" sx={{ py: 0.25, px: 1, fontSize: '0.75rem' }}>
              Bode
            </ToggleButton>
            <ToggleButton value="nyquist" sx={{ py: 0.25, px: 1, fontSize: '0.75rem' }}>
              Nyquist
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>
      )}

      {/* Channel toggles — hidden in calibration mode */}
      {!isCalibration && (
        <FormControl component="fieldset" size="small">
          <FormLabel sx={{ fontSize: '0.75rem' }}>Channels</FormLabel>
          <FormGroup row>
            {CHANNELS.map((ch) => (
              <FormControlLabel
                key={ch}
                control={
                  <Checkbox
                    size="small"
                    checked={visibleChannels.has(ch)}
                    onChange={(e) => onChannelToggle(ch, e.target.checked)}
                  />
                }
                label={ch}
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
              />
            ))}
          </FormGroup>
        </FormControl>
      )}

      {/* Phase unit — Bode, Combined, and Bode-delta */}
      {!isCalibration && (chartType === 'bode' || chartType === 'combined' || (chartType === 'delta' && deltaMode === 'bode')) && (
        <FormControl size="small">
          <FormLabel sx={{ fontSize: '0.75rem' }}>Phase</FormLabel>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={phaseUnit}
            onChange={(_, v) => v && onPhaseUnitChange(v as 'rad' | 'deg')}
          >
            <ToggleButton value="rad" sx={{ py: 0.25, px: 1, fontSize: '0.75rem' }}>
              rad
            </ToggleButton>
            <ToggleButton value="deg" sx={{ py: 0.25, px: 1, fontSize: '0.75rem' }}>
              deg
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>
      )}

      {/* Y scale — Bode and Combined (delta is always linear) */}
      {!isCalibration && (chartType === 'bode' || chartType === 'combined') && (
        <FormControl size="small">
          <FormLabel sx={{ fontSize: '0.75rem' }}>Y Scale</FormLabel>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={yScale}
            onChange={(_, v) => v && onYScaleChange(v as 'linear' | 'log')}
          >
            <ToggleButton value="linear" sx={{ py: 0.25, px: 1, fontSize: '0.75rem' }}>
              Lin
            </ToggleButton>
            <ToggleButton value="log" sx={{ py: 0.25, px: 1, fontSize: '0.75rem' }}>
              Log
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>
      )}

      {/* Spacer */}
      <Box sx={{ flexGrow: 1 }} />
    </Box>
  );
}
